import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container, Typography, TextField, Button,
  Divider, Alert, Box,
  Avatar, Chip, InputAdornment, Paper, List,
  ListItem, ListItemButton, ListItemText, CircularProgress,
  IconButton, useMediaQuery, useTheme,
} from '@mui/material';
import SearchIcon      from '@mui/icons-material/Search';
import SendIcon        from '@mui/icons-material/Send';
import ArrowBackIcon   from '@mui/icons-material/ArrowBack';
import { useTranslation } from 'react-i18next';
import SidebarLayout from '../components/sidebarLayout';
import { getAllPrenotazioni } from '../APIs/ReservationsAPI';
import { getAllTavoli }        from '../APIs/TablesAPI';
import { getAllStanze }        from '../APIs/RoomsAPI';
import { getNotesByReservation, createNote } from '../APIs/ComunicazioniAPI';
import { SetupWSEventListener } from '../utils/setupWSEventListener';
import { formatDateTime24 } from '../components/dateTimeDisplay';


function Comunicazioni() {
  const { t } = useTranslation();
  const theme   = useTheme();
  // Side-by-side on md+ (≥900px), stacked on smaller (iPad portrait, phone)
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const token  = localStorage.getItem('accessToken');
  const tenant = localStorage.getItem('tenantId');
  const userId = localStorage.getItem('id');

  const [search,              setSearch]              = useState('');
  const [reservations,        setReservations]        = useState([]);
  const [loadingRes,          setLoadingRes]          = useState(true);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showChat,            setShowChat]            = useState(false); // mobile nav state
  const [messages,            setMessages]            = useState([]);
  const [loadingMsgs,         setLoadingMsgs]         = useState(false);
  const [newMessage,          setNewMessage]          = useState('');
  const [error,               setError]               = useState('');
  const [msgCountMap,         setMsgCountMap]         = useState({});
  const [tableMap,            setTableMap]            = useState({});
  const [roomMap,             setRoomMap]             = useState({});

  const messagesEndRef   = useRef(null);
  const selectedResIdRef = useRef(null);
  const inputRef         = useRef(null);

  const readCountRef = useRef(
    (() => {
      try { return JSON.parse(localStorage.getItem('comm_read_counts') ?? '{}'); }
      catch { return {}; }
    })()
  );

  const saveReadCounts = () => {
    localStorage.setItem('comm_read_counts', JSON.stringify(readCountRef.current));
  };

  useEffect(() => {
    selectedResIdRef.current = selectedReservation?.id ?? null;
  }, [selectedReservation]);

  const formatTableSlot = useCallback((tr) => {
    const table     = tableMap[tr.tableId] ?? tr.table ?? {};
    const tableName = table?.name ?? tr.table?.name ?? '';
    if (!tableName) return '';
    const roomId   = table?.roomId ?? tr.table?.roomId ?? null;
    const room     = roomMap[roomId] ?? table?.room ?? tr.table?.room ?? null;
    const roomName = room?.name ?? '';
    const cleanTableName = /^\d+ (\d+)$/.test(tableName)
      ? tableName.replace(/^\d+ /, '')
      : tableName;
    return roomName
      ? `${t('room')} ${roomName} · ${t('table')} ${cleanTableName}`
      : `${t('table')} ${cleanTableName}`;
  }, [tableMap, roomMap, t]);

  const buildCountMap = useCallback((data) => {
    const map = {};
    data.forEach(res => {
      const total    = res.notes?.length ?? 0;
      const lastRead = readCountRef.current[res.id] ?? 0;
      const unread   = total - lastRead;
      if (unread > 0) map[res.id] = unread;
    });
    setMsgCountMap(map);
  }, []);

  const fetchReservations = useCallback(async () => {
    try {
      const data = await getAllPrenotazioni(token, tenant);
      const list = Array.isArray(data) ? data : [];
      setReservations(list);
      buildCountMap(list);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingRes(false);
    }
  }, [token, tenant, buildCountMap]);

  const fetchTablesAndRooms = useCallback(async () => {
    try {
      const [tables, rooms] = await Promise.all([
        getAllTavoli(token, tenant),
        getAllStanze(token, tenant),
      ]);
      if (Array.isArray(tables)) {
        const map = {};
        tables.forEach(t => { map[t.id] = t; });
        setTableMap(map);
      }
      if (Array.isArray(rooms)) {
        const map = {};
        rooms.forEach(r => { map[r.id] = r; });
        setRoomMap(map);
      }
    } catch (e) {
      console.error('Failed to load tables/rooms:', e);
    }
  }, [token, tenant]);

  const fetchMessages = useCallback(async (reservationId) => {
    setLoadingMsgs(true);
    try {
      const data   = await getNotesByReservation(reservationId, token, tenant);
      const list   = Array.isArray(data) ? data : [];
      const sorted = [...list].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      setMessages(sorted);
    } catch {
      setMessages([]);
    } finally {
      setLoadingMsgs(false);
    }
  }, [token, tenant]);

  useEffect(() => {
    fetchReservations();
    fetchTablesAndRooms();
    const cleanup = SetupWSEventListener(() => {
      fetchReservations();
      if (selectedResIdRef.current) fetchMessages(selectedResIdRef.current);
    });
    return cleanup;
  }, [fetchReservations, fetchTablesAndRooms, fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const selectReservation = (res) => {
    setSelectedReservation(res);
    setNewMessage('');
    setError('');
    fetchMessages(res.id);
    readCountRef.current[res.id] = res.notes?.length ?? 0;
    saveReadCounts();
    setMsgCountMap(prev => ({ ...prev, [res.id]: 0 }));
    // On mobile: navigate to chat view
    if (isMobile) setShowChat(true);
  };

  const handleBack = () => {
    setShowChat(false);
    setSelectedReservation(null);
  };

  const handleSend = async () => {
    if (!newMessage.trim()) {
      setError(t('comunicazioni.error_empty'));
      return;
    }
    setError('');
    try {
      await createNote(
        selectedReservation.id,
        { note: newMessage.trim() },
        token,
        tenant,
      );
      setNewMessage('');
      fetchMessages(selectedReservation.id);
      fetchReservations();
    } catch {
      setError(t('comunicazioni.error_publish'));
    }
  };

  const getLastNoteDate = (res) => {
    if (!res.notes?.length) return new Date(res.createdAt ?? 0);
    return new Date(Math.max(...res.notes.map(n => new Date(n.createdAt).getTime())));
  };

  const filteredReservations = reservations
    .filter(r => {
      const q    = search.toLowerCase();
      const name = `${r.client?.name ?? ''} ${r.client?.surname ?? ''}`.toLowerCase();
      const room = (r.tableReservations?.map(tr => tr.table?.name).join(' ') ?? '').toLowerCase();
      return name.includes(q) || room.includes(q);
    })
    .sort((a, b) => getLastNoteDate(b) - getLastNoteDate(a));

  // ── Shared panel height — dvh accounts for iPad virtual keyboard ──
  const panelHeight = 'calc(100dvh - 220px)';

  // ── LEFT PANEL ──
  const leftPanel = (
    <Paper
      elevation={1}
      sx={{
        width:      isMobile ? '100%' : 300,
        flexShrink: 0,
        display:    isMobile && showChat ? 'none' : 'flex',
        flexDirection: 'column',
        overflow:   'hidden',
        borderRadius: 2,
        height:     panelHeight,
      }}
    >
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          {t('comunicazioni.select_reservation')}
        </Typography>
        <TextField
          fullWidth size="small"
          placeholder={t('comunicazioni.search_reservation')}
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <List sx={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', p: 0 }}>
        {loadingRes ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress size={24} />
          </Box>
        ) : filteredReservations.length === 0 ? (
          <ListItem>
            <ListItemText primary={
              <Typography variant="body2" color="text.secondary">
                {t('comunicazioni.no_reservations')}
              </Typography>
            } />
          </ListItem>
        ) : (
          filteredReservations.map(res => {
            const msgCount   = msgCountMap[res.id] ?? 0;
            const isSelected = selectedReservation?.id === res.id;
            const clientName = res.client
              ? `${res.client.name} ${res.client.surname}`
              : `${t('reservation')} #${res.id}`;
            const dateStr = res.startDate
              ? new Date(res.startDate).toLocaleDateString()
              : '';

            return (
              <ListItemButton
                key={res.id}
                selected={isSelected}
                onClick={() => selectReservation(res)}
                sx={{
                  borderBottom: '1px solid',
                  borderColor:  'divider',
                  minHeight:    56, // ≥44px touch target
                }}
              >
                <ListItemText
                  primary={
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 190 }}>
                        {clientName}
                      </Typography>
                      {msgCount > 0 && (
                        <Chip label={msgCount} size="small" color="primary" sx={{ height: 20, fontSize: '0.7rem', ml: 0.5 }} />
                      )}
                    </Box>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {res.tableReservations?.map(formatTableSlot).filter(Boolean).join(', ')}
                      {dateStr ? ` · ${dateStr}` : ''}
                    </Typography>
                  }
                />
              </ListItemButton>
            );
          })
        )}
      </List>
    </Paper>
  );

  // ── RIGHT PANEL ──
  const rightPanel = (
    <Paper
      elevation={1}
      sx={{
        flex:    1,
        width:   isMobile ? '100%' : undefined,
        display: isMobile && !showChat ? 'none' : 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: 2,
        height:  panelHeight,
      }}
    >
      {!selectedReservation ? (
        <Box display="flex" alignItems="center" justifyContent="center" flex={1} sx={{ color: 'text.secondary' }}>
          <Box textAlign="center">
            <Typography variant="h6" gutterBottom>💬</Typography>
            <Typography variant="body2">{t('comunicazioni.select_to_start')}</Typography>
          </Box>
        </Box>
      ) : (
        <>
          {/* Header */}
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Back button — mobile only */}
            {isMobile && (
              <IconButton onClick={handleBack} sx={{ mr: 0.5, minWidth: 44, minHeight: 44 }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <Box flex={1} minWidth={0}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {selectedReservation.client
                  ? `${selectedReservation.client.name} ${selectedReservation.client.surname}`
                  : `${t('reservation')} #${selectedReservation.id}`}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {selectedReservation.tableReservations?.map(formatTableSlot).filter(Boolean).join(', ')}
                {selectedReservation.startDate
                  ? ` · ${formatDateTime24(selectedReservation.startDate)} → ${formatDateTime24(selectedReservation.endDate)}`
                  : ''}
                {selectedReservation.guests
                  ? ` · ${selectedReservation.guests} ${t('guests')}`
                  : ''}
              </Typography>
            </Box>
          </Box>

          {/* Messages */}
          <Box sx={{
            flex: 1,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch', // smooth scroll on iOS
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}>
            {loadingMsgs ? (
              <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
                <CircularProgress size={24} />
              </Box>
            ) : messages.length === 0 ? (
              <Box display="flex" alignItems="center" justifyContent="center" flex={1}>
                <Typography variant="body2" color="text.secondary">
                  {t('comunicazioni.no_notes')}
                </Typography>
              </Box>
            ) : (
              messages.map((msg, i) => {
                const isMe       = String(msg.userId) === String(userId);
                const authorName = [msg.user?.name, msg.user?.surname]
                  .filter(Boolean)
                  .join(' ')
                  || t('comunicazioni.user_fallback', { id: msg.userId });
                return (
                  <Box
                    key={msg.id ?? i}
                    display="flex"
                    flexDirection={isMe ? 'row-reverse' : 'row'}
                    alignItems="flex-start"
                    gap={1}
                  >
                    <Avatar sx={{ width: 32, height: 32, fontSize: '0.75rem', bgcolor: isMe ? 'primary.main' : 'grey.400', flexShrink: 0 }}>
                      {authorName.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ maxWidth: '70%', display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25 }}>
                        {authorName} · {msg.createdAt ? formatDateTime24(msg.createdAt) : ''}
                      </Typography>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          width: 'fit-content',
                          maxWidth: '100%',
                          bgcolor: isMe ? 'primary.main' : 'grey.100',
                          color:   isMe ? 'white' : 'text.primary',
                          borderRadius: 2,
                          borderTopRightRadius: isMe ? 0 : 2,
                          borderTopLeftRadius:  isMe ? 2 : 0,
                        }}
                      >
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {msg.note ?? msg.message}
                        </Typography>
                      </Paper>
                    </Box>
                  </Box>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </Box>

          {/* Input */}
          <Divider />
          <Box sx={{ p: 2 }}>
            {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
            <Box display="flex" gap={1} alignItems="flex-end">
              <TextField
                inputRef={inputRef}
                fullWidth multiline maxRows={4} size="small"
                placeholder={t('comunicazioni.write_placeholder')}
                value={newMessage}
                onChange={e => setNewMessage(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                // Better keyboard on iPad
                inputProps={{ enterKeyHint: 'send' }}
              />
              <Button
                variant="contained"
                onClick={handleSend}
                sx={{ minWidth: 44, minHeight: 44, px: 2 }}
                endIcon={<SendIcon />}
              >
                {t('comunicazioni.publish')}
              </Button>
            </Box>
          </Box>
        </>
      )}
    </Paper>
  );

  return (
    <SidebarLayout className="admin-responsive-page">
      <Container sx={{ mt: { xs: 2, md: 4 } }}>
        <Typography variant="h4" gutterBottom>
          💬 {t('comunicazioni.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('comunicazioni.subtitle')}
        </Typography>

        <Box sx={{ display: 'flex', gap: isMobile ? 0 : 3 }}>
          {leftPanel}
          {rightPanel}
        </Box>
      </Container>
    </SidebarLayout>
  );
}

export default Comunicazioni;
